import { useQueryState } from "@msuite/ui/libs/nuqs";
import { guardArray, uniqueArrayByKey } from "@msuite/utils/array";
import type { GetTeamsQuerySchema } from "@msuite/utils/schemas";
import { TEAM_FILTER_ALL, TEAM_FILTER_UNASSIGNED } from "@msuite/utils/schemas";
import { useEffect, useState } from "react";
import { useTeams } from "./use-teams";

/** Options Interface */
interface UseTeamPickerOptions {
	/**
	 * Offer "all teams" as the first entry and open on it. Without it the picker opens on the
	 * alphabetically first team, which is what a screen needs that cannot render without a
	 * concrete one. The screen decides, and on the people list that decision is `read User`.
	 */
	withAllOption?: boolean;
	/**
	 * Offer a last entry holding the members no team holds. A team is optional on a person, so
	 * a picker made only of teams leaves everybody who has none in no tab at all.
	 */
	withUnassignedOption?: boolean;
}

export function useTeamPicker(query?: GetTeamsQuerySchema, options?: UseTeamPickerOptions) {
	/** State */
	const [storedTeamId, setTeamId] = useQueryState("team-picker");
	const [isReady, setIsReady] = useState<boolean>(!isOffered(storedTeamId, options));

	/** Query */
	const { teams } = useTeams(query);

	/** Effects */
	useEffect(() => {
		if (!teams) return;
		if (isOffered(storedTeamId, options)) {
			setIsReady(true);
			return;
		}
		const initialTeamId = options?.withAllOption ? TEAM_FILTER_ALL : teams.at(0)?.teamId;
		if (initialTeamId) {
			setTeamId(initialTeamId);
			setIsReady(true);
		}
	}, [teams, storedTeamId, options?.withAllOption, options?.withUnassignedOption]);

	/** Computed */
	const teamId = isOffered(storedTeamId, options) ? storedTeamId : null;
	// What the selection means to a member query: "all" is the absence of a filter and stops
	// here, while a team id and the unassigned literal both travel to the server.
	const teamIdFilter = teamId === TEAM_FILTER_ALL ? undefined : (teamId ?? undefined);

	/** Return */
	return {
		isReady,
		setTeamId,
		teamId,
		teamIdFilter,
		teams: uniqueArrayByKey(guardArray(teams), "teamId"),
		withAllOption: options?.withAllOption === true,
		withUnassignedOption: options?.withUnassignedOption === true,
	};
}

/**
 * Whether a stored value is one this picker actually has an entry for. `team-picker` is a single
 * query parameter shared by every screen that carries a picker, so a screen can be opened on a
 * value it does not offer — "all", left behind by the people list, on a screen that needs one
 * concrete team, or on a screen that does not offer it to this caller. Such a value reads as
 * nothing selected, and the effect above puts the screen on a team instead.
 */
function isOffered(value: string | null, options?: UseTeamPickerOptions) {
	if (!value) return false;
	if (value === TEAM_FILTER_ALL) return options?.withAllOption === true;
	if (value === TEAM_FILTER_UNASSIGNED) return options?.withUnassignedOption === true;
	return true;
}

export type TeamPickerController = ReturnType<typeof useTeamPicker>;
